/**
 * The sol phase, the season and the sun scrub: where the app reads and
 * moves the drawn instant's solar answer for the focused body.
 *
 * Reads the closure it was cut out of through `SolarScrubDeps`. The
 * `atlasCamera` probe members that read nothing outside this seam ride on
 * `probe`, which the composition root in src/atlas.ts folds back into the
 * one object the tools/preview/dojo arms read.
 */

import * as THREE from 'three';

import type { AtlasState } from '../atlas';
import { MISSION_ANCHOR_T, SOL_SECONDS, SOLS_PER_MARS_YEAR, solAt } from '../world/clock.ts';
import { heliocentricPeriodS, solarDayS } from '../world/solarDay.ts';
import type { Orbit } from '../world/types';
import { softClimate, solarDeclinationDeg, type PlotClimate } from './plotField.ts';
import { wrap180HalfOpen } from '../math/angles.ts';
import { elementsAt } from '../world/ephemeris.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { DescentAnchorHandle } from './descentAnchor.ts';
import type { DojoPaintsHandle } from './dojoPaints.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { OrreryBody } from './orrery';
import { TEST_SWITCHES } from '../urlFlags.ts';

/** `?seasonecliptic=1`: every body's Ls off its sub-solar latitude, Mercury's
 *  included, the path every season-carrying row ran before this file read the
 *  orbit instead. `tools/preview/season-orbit-check.mjs --inject ecliptic`
 *  drives it as the control. */
const SEASON_PARAMS = typeof location === 'undefined' ? null : new URLSearchParams(location.search);
const SEASON_ECLIPTIC = SEASON_PARAMS !== null && TEST_SWITCHES
  && SEASON_PARAMS.get('seasonecliptic') === '1';
/** How close `scrubToLs` lands to the season asked for, degrees of Ls. */
const LS_SCRUB_TOL_DEG = 0.02;
/** Sun-latitude samples across the year `scrubToSeasonFor` scans, half either side of the instant. */
const SEASON_SCAN_STEPS = 144;
/** Scene axes: `eclipticToScene` puts the ecliptic pole on +y. */
const ECLIPTIC_POLE = new THREE.Vector3(0, 1, 0);
const SOL_PHASE_AT_ANCHOR = 0.34;
const LS_AT_ANCHOR_DEG = 120;

function wrap01(v: number): number {
  return v - Math.floor(v);
}
function wrap360(v: number): number {
  return ((v % 360) + 360) % 360;
}

/** A season orbit's secular fit with its osculating sets left off, one copy per orbit. */
const secularFits = new WeakMap<Orbit, Orbit>();
function secularFit(orbit: Orbit): Orbit {
  if (orbit.sets === undefined) return orbit;
  let fit = secularFits.get(orbit);
  if (!fit) {
    fit = { ...orbit, sets: undefined };
    secularFits.set(orbit, fit);
  }
  return fit;
}

const _sunScene = new THREE.Vector3();
/** Read by `src/atlas/hexView.ts`'s quad outlines, which draw against the
 *  same instant's sun this module last solved for. */
export const _sunFixed = new THREE.Vector3();
const _northScene = new THREE.Vector3();
const _equinox = new THREE.Vector3();
const _sunQ = new THREE.Quaternion();

export interface SolarScrubDeps {
  readonly YAXIS: THREE.Vector3;
  readonly focusRig: FocusRigHandle;
  readonly state: AtlasState;
  readonly bodyReaders: BodyReadersHandle;
  /** The instant the Atlas draws and the scrubs below move: a preview's while
   *  one is up, the one clock's otherwise (src/atlas.ts, `viewClock`). */
  readonly clock: { readonly t: number; scrub(deltaSeconds: number): void; setInstant(t: number): void };
  readonly bodyScene: BodySceneHandle;
  /** Both built below this seam's own call site in atlas.ts: getters there,
   *  read here only at the use site. */
  readonly dojoPaints: DojoPaintsHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
}

export interface SolarScrubHandle {
  readonly plotCtlDebug: { plotFieldGate: boolean; marsSol: boolean };
  readonly _litSun: THREE.Vector3;
  readonly _litUp: THREE.Vector3;
  readonly solar: { solPhase: number; lsDeg: number; overlay: number; contour: number; exag: number };
  solarLatLon(): { lat: number; lon: number } | null;
  syncSolar(): void;
  solsSinceAnchor(): number;
  bodySolarDayS(): number | null;
  bodyYearS(): number;
  seasonMoves(): boolean;
  solClockLabel(phase: number): string;
  scrubToSolPhase(phase: number): void;
  scrubToLs(lsDeg: number): boolean;
  /** The sun's latitude over the focused body: the orrery's where it turns the body, else the season model's. */
  sunDeclinationDeg(climate: PlotClimate): number;
  scrubToSunlight(latDeg: number, lonDeg: number, minAltDeg: number): { err: string } | {
    t: number; altitudeDeg: number; requestedDeg: number; solPhase: number; lsDeg: number;
    declinationDeg: number; seasonMoved: boolean; passes: number;
  };
  /** The `atlasCamera` members that read nothing outside this module. */
  readonly probe: {
    readonly solDayS: number;
    sunAltitudeOver(latDeg: number, lonDeg: number): number | null;
  };
}

export function createSolarScrub(deps: SolarScrubDeps): SolarScrubHandle {
  const { YAXIS, state } = deps;
  const { focusBodies, focusCam } = deps.focusRig;
  const { bodyClimate, ladderBody, rowById } = deps.bodyReaders;

  /**
   * The plot's sun, as a view of the one clock.
   *
   * `solPhase` and `lsDeg` used to be local numbers the sweep buttons walked, so
   * the era the Atlas drew and the era the HUD printed were unrelated. Phase 1
   * made them a function of `t`; `syncSolar` makes them a function of the
   * EPHEMERIS at `t`, so the plot's analytic angles and the orrery's geometry
   * are one sun rather than two that disagreed by up to 108 degrees
   * (docs/roadmap/01-world-model.md, "Ephemerides and time").
   */
  // Sol phase 0..1 (0.5 = local noon) and areocentric longitude of the season.
  // Ls 251 is perihelion, which for Mars is southern summer and the dusty half
  // of the year — the flux swing across Ls is ~45%, so this slider moves real
  // numbers, not a colour grade.
  // overlay is a STAGE, not a flag. Off / parcels / survey — because the honest
  // answer to "the grid dominates" is not a dimmer, it is being able to see the
  // property lines without the survey on top of them.
  // The wash is drawn on the whole-file mesh, not on the leaves it takes over
  // from, so `overlay` starts at stage 0 and the person turns it on.
  const solar = { solPhase: 0.34, lsDeg: 120, overlay: 0, contour: 0, exag: 1.0 };

  /** Sols since the mission anchor, which is where both anchors are taken. */
  function solsSinceAnchor(): number {
    return solAt(deps.clock.t) - solAt(MISSION_ANCHOR_T);
  }

  /**
   * The place on the body the sun is read AT: the focused site, or the ladder's
   * own aim where the body has no site to focus.
   *
   * `anchorLatLon` answers only from `sites.json`, and the fallback it returns
   * null for is not a rare case - Venus, Earth and Pluto have no site at all.
   * `syncSolar` then went back to the anchored MARS count for both its numbers,
   * so the sky at SURFACE turned under a clock the orrery knew nothing about and
   * the two suns read 147 degrees of altitude apart. The aim is the only
   * latitude a siteless body has, and it is the one the descent lands on.
   */
  function solarLatLon(): { lat: number; lon: number } | null {
    // The REGION's centre and not what the ladder names: a hand-off standing
    // off the survey still has the sky it always had over it, and the hour
    // angle is a body-scale answer that the claim gate has no business moving.
    const a = deps.descentAnchorHandle.regionAnchorLatLon();
    if (a) return a;
    // `descent-dojo --sky --inject sliding-sun`: the camera's own anchor, which
    // above the ORBIT edge is re-derived at the spin rate and so cannot carry
    // an hour angle. `solarSite` is that anchor held still (`syncDescentAnchor`).
    if (!deps.dojoPaints.skyDebug.slidingSun && deps.descentAnchorHandle.solarSite && deps.descentAnchorHandle.solarSite.body === focusCam.state.bodyId) {
      return { lat: deps.descentAnchorHandle.solarSite.lat, lon: deps.descentAnchorHandle.solarSite.lon };
    }
    const { latDeg, lonDeg } = focusCam.state;
    return latDeg === null || lonDeg === null ? null : { lat: latDeg, lon: lonDeg };
  }

  /**
   * The plot's two numbers, as a view of the ephemeris sun.
   *
   * The app used to hold two suns. `solPhase` counted mean sols from an
   * authored anchor and `lsDeg` counted degrees linearly beside it, so
   * `sunAngles(lat, solarDeclinationDeg(lsDeg), H)` - which lights the plot, the
   * sky and, in WP5, the shadows - was a second answer to the question the
   * orrery already answers from real ephemerides and a WGCCRE pole. They
   * disagreed by up to 108 degrees of altitude: the orrery's local noon at
   * Jezero fell at sol phase 0.15 and the plot's at 0.50.
   *
   * Both are now read off the orrery at the focused site, and `sunAngles` is
   * left alone because the spherical trig it does is EXACTLY the geometry the
   * orrery has: altitude from the site's radial against the direction to Sol.
   *
   *   solPhase   0.5 plus the site's hour angle, the sub-solar longitude
   *              against the site's own, in the body-fixed frame every lat/lon
   *              in this file is written in (`latLonOfHit`). Scrubbing the
   *              clock by a sol still moves it by one, because a sol IS the
   *              solar day the body's rotation and its orbit make between them.
   *   lsDeg      inverted out of the sub-solar LATITUDE rather than integrated
   *              beside it, on a row with no season block and no orbit; there
   *              `solarDeclinationDeg(lsDeg)` returns that latitude back to float
   *              precision. A season row reads Ls off the orbit instead.
   *              Which half of the year it is comes from the sun's ecliptic
   *              longitude measured from the ascending node of the body's
   *              equator, where the northern spring equinox sits.
   *
   * Before the registry lands there is no ephemeris to ask and the panel still
   * needs a sun, so the old anchored count stands in until it does.
   */
  function syncSolar(): void {
    const node = deps.bodyScene.state.bodyNode;
    // `descent-dojo --sky --inject legacy-sun`: the anchored count on purpose,
    // which is what a siteless body used to get and what the `solar` bar reads.
    const a = deps.dojoPaints.skyDebug.legacySun ? null : solarLatLon();
    if (!node || !a || !node.rotationKnown) {
      const d = solsSinceAnchor();
      solar.solPhase = wrap01(SOL_PHASE_AT_ANCHOR + d);
      solar.lsDeg = wrap360(LS_AT_ANCHOR_DEG + (d / SOLS_PER_MARS_YEAR) * 360);
      return;
    }
    // The ladder body's own soft season at tier 0, where there is no focused
    // body to read one off: without it `solPhase` and `lsDeg` hold whatever
    // the last focused body left them at.
    const ladderRow = rowById(ladderBody());
    const climate = bodyClimate() ?? (ladderRow ? softClimate(ladderRow, rowById).climate : null);
    if (!climate) return;
    // This frame's rotation, not last frame's: `setSol` scrubs the clock and
    // reads the phase back in the same call, and `propagate` is a no-op once
    // the instant has already been solved.
    deps.bodyScene.state.orrery?.propagate();
    _sunScene.set(-node.world.x, -node.world.y, -node.world.z);
    if (_sunScene.lengthSq() === 0) return;
    _sunScene.normalize();
    _northScene.copy(YAXIS).applyQuaternion(node.quaternion);
    const sinDec = THREE.MathUtils.clamp(_sunScene.dot(_northScene), -1, 1);

    _sunFixed.copy(_sunScene).applyQuaternion(_sunQ.copy(node.quaternion).invert());
    const subLonDeg = (Math.atan2(-_sunFixed.z, _sunFixed.x) * 180) / Math.PI;
    // An hour angle ADVANCES with the clock, and on a retrograde rotator the
    // sub-solar longitude advances the other way, so the difference runs
    // backwards and the clock counted down. Which WAY that sun then crosses
    // the sky is `localSunAzimuth`'s, not this sign's.
    const spin = (node.row.rotation.W_rate_deg_day ?? 0) < 0 ? -1 : 1;
    solar.solPhase = wrap01(0.5 + (spin * (wrap360(a.lon - subLonDeg + 180) - 180)) / 360);

    // Every row with a season reads its Ls as the true anomaly from perihelion on its own
    // orbit, which needs neither a pole nor ECLIPTIC_POLE as a stand-in for one: Mercury,
    // Venus, Mars and Pluto's own orbital planes stand degrees off ECLIPTIC_POLE (Mars 1.85,
    // Venus 3.40, Mercury 7.01, Pluto 17.14), which the old declination inversion could not
    // see, and a solstice's flat arcsin turned a one- or two-degree mismatch into a
    // double-digit Ls swing; Earth's orbit stands within a thousandth of a degree of the
    // ecliptic and reads the same way. A moon carries no heliocentric orbit of its own
    // (`orbit.parent_relative`), so it reads its parent's - the barycentre's season this
    // row's own `climate.lsPerihelionDeg` already names.
    const orbit = node.row.orbit;
    const jd = deps.bodyScene.state.orrery?.jdTdb();
    const seasonOrbit = orbit?.parent_relative ? rowById(node.row.parent)?.orbit : orbit;
    if (!SEASON_ECLIPTIC && jd !== undefined && orbit && node.row.season && seasonOrbit) {
      // `ls_perihelion_deg` counts from the secular fit's perihelion, so the anomaly added to it is
      // the secular one: Saturn's osculating perihelion wanders up to 5.3 deg off that fit's.
      const el = elementsAt(secularFit(seasonOrbit), jd);
      const nu = 2 * Math.atan2(Math.sqrt(1 + el.e) * Math.sin(el.E / 2), Math.sqrt(1 - el.e) * Math.cos(el.E / 2));
      solar.lsDeg = wrap360(climate.lsPerihelionDeg + THREE.MathUtils.radToDeg(nu));
      return;
    }
    const sinObl = Math.sin((climate.obliquityDeg * Math.PI) / 180);
    const ls = (Math.asin(THREE.MathUtils.clamp(sinDec / sinObl, -1, 1)) * 180) / Math.PI;
    // `?seasonecliptic=1` only, now that every season-carrying row above reads its Ls off an
    // orbit: the ecliptic stands in for the body's own orbital plane, which only Earth's IS.
    _equinox.copy(_northScene).cross(ECLIPTIC_POLE).normalize();
    solar.lsDeg = wrap360(_sunScene.dot(_equinox) < 0 ? 180 - ls : ls);
  }

  /**
   * The focused body's own solar day, seconds.
   *
   * A sol phase is a fraction of the body's own noon-to-noon day, so scrubbing
   * the clock to reach one has to be in that body's seconds and not in Mars's:
   * a Mars sol is 3.5 percent of a lunar one, so `SOL_SECONDS` moved the Moon's
   * sun by 12 degrees of hour angle for a whole phase asked for. The clock's
   * own Mars sol stands in only before the registry lands, which is the same
   * case `syncSolar` falls back to the anchored count for; every focused row
   * answers for itself through `bodySolarDayS` below, published or derived.
   */
  function solDaySeconds(): number {
    return bodySolarDayS() ?? SOL_SECONDS;
  }

  /**
   * `surface-clamp --solctl`'s two controls.
   *
   * `plotFieldGate` puts the panel back behind a loaded plot field, which is
   * what hid the whole thing on every body with no `sites.json` row; `marsSol`
   * puts the Mars sol back under the clock, which is what the SOL row used to
   * count in before the registry could be asked.
   */
  const plotCtlDebug = { plotFieldGate: false, marsSol: false };

  /**
   * The focused body's own solar day in seconds, or null where its row cannot
   * supply one.
   *
   * Null is Sol's answer and nobody else's: every other row either publishes a
   * solar day or has the rotation and the orbit to derive one from
   * (`src/world/solarDay.ts`). The old stand-in was the SIDEREAL period, which
   * is 58.65 Earth days on Mercury against a solar day of 175.94 - a clock
   * three times too fast on the one row the panel exists to read.
   */
  function bodySolarDayS(): number | null {
    if (plotCtlDebug.marsSol) return SOL_SECONDS;
    const row = rowById(state.bodyId);
    return row ? solarDayS(row, rowById) : null;
  }

  /**
   * Local time on the focused body, hours and minutes of ITS own solar day.
   *
   * `24.66` used to stand here, so the plot card read a Mars sol's clock on
   * every body: the Moon's solar day is 708.7 h, so a lunar noon read 12:19.
   * Hours run past 24 because a lunar hour of local time is not an Earth hour
   * and pretending otherwise is the same lie in a shorter string.
   */
  function solClockLabel(phase: number): string {
    const hours = (((phase % 1) + 1) % 1) * (solDaySeconds() / 3600);
    const hh = Math.floor(hours);
    const mm = Math.floor((hours % 1) * 60);
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }

  /**
   * Scrub the clock to a sol phase on the focused body.
   *
   * Iterated rather than solved: `solPhase` is read back off the orrery's own
   * rotation, so the body's orbital motion during a 29.5-day lunar scrub moves
   * the sub-solar longitude the mean solar day does not account for. Three
   * passes put it inside a thousandth of a phase on both bodies; a pass that
   * does not improve stops.
   */
  function scrubToSolPhase(phase: number): void {
    const solS = solDaySeconds();
    for (let i = 0; i < 4; i++) {
      let d = phase - solar.solPhase;
      if (d > 0.5) d -= 1;
      if (d < -0.5) d += 1;
      if (Math.abs(d) < 1e-4) break;
      deps.clock.scrub(d * solS);
      syncSolar();
    }
  }

  const _litUp = new THREE.Vector3();
  const _litSun = new THREE.Vector3();

  /**
   * The sun's altitude over a body-fixed point, degrees.
   *
   * Not `sunAltitudeDeg`, which answers for the eye's own ground point. Above
   * the ORBIT band edge the rig is anchored on the body's CENTRE and its basis
   * is the pole, so the eye holds a direction the body turns under rather than
   * one it carries: that reading is the sun-body-camera phase angle and a
   * scrub of the clock cannot move it. A point the body carries is what a
   * check asking for daylight on the ground means.
   */
  function sunAltitudeOverDeg(latDeg: number, lonDeg: number): number | null {
    const node = deps.bodyScene.state.bodyNode;
    if (!node) return null;
    const s = focusBodies.surface(node.row.id, latDeg, lonDeg);
    if (!s) return null;
    _litSun.set(-node.world.x, -node.world.y, -node.world.z);
    _litUp.set(s.x - node.world.x, s.y - node.world.y, s.z - node.world.z);
    if (_litSun.lengthSq() === 0 || _litUp.lengthSq() === 0) return null;
    return (Math.asin(THREE.MathUtils.clamp(
      _litUp.normalize().dot(_litSun.normalize()), -1, 1)) * 180) / Math.PI;
  }

  /**
   * The focused body's own year, seconds: its motion around the Sun, which for
   * a moon is its primary's. The Mars year stands in only before the registry
   * lands, the case `syncSolar` falls back to the anchored count for.
   */
  function bodyYearS(): number {
    const row = rowById(state.bodyId);
    return (row ? heliocentricPeriodS(row, rowById) : null) ?? SOLS_PER_MARS_YEAR * SOL_SECONDS;
  }

  /**
   * Whether the clock moves the focused body's Ls at all: true where the
   * orbit branch in `syncSolar` runs (an orbit of its own, not
   * parent-relative) or the obliquity inversion has a nonzero obliquity to
   * invert. A row with no `season` block, or one whose obliquity is 0 and
   * whose orbit is parent-relative (so neither path runs), is read with the
   * sun held over its equator, and `syncSolar` pins its Ls at 90 or 270
   * whatever the date.
   */
  function seasonMoves(): boolean {
    const row = rowById(state.bodyId);
    if (!row?.season) return false;
    if (row.orbit && !row.orbit.parent_relative) return true;
    return row.season.obliquity_deg !== 0;
  }

  /**
   * Scrub the clock to an Ls on the focused body; false where it cannot get
   * within `LS_SCRUB_TOL_DEG` of it, and at once, the clock untouched, where
   * no date moves the Ls.
   *
   * The first step is the body's own year, and each later one takes its rate
   * from what the step before it bought, since Ls runs half again faster than
   * the mean at Mercury's perihelion and a third slower at its aphelion. A sol
   * cannot stand in for the rate: Mercury's is two of its years, so the Ls a
   * sol of scrub reads back wraps to nothing.
   */
  function scrubToLs(lsDeg: number): boolean {
    syncSolar();
    if (!seasonMoves()) return false;
    let rate = 360 / bodyYearS();
    for (let i = 0; i < 8; i++) {
      const d = wrap180HalfOpen(lsDeg - solar.lsDeg);
      if (Math.abs(d) <= LS_SCRUB_TOL_DEG) return true;
      const ls0 = solar.lsDeg;
      const dt = d / rate;
      deps.clock.scrub(dt);
      syncSolar();
      const moved = wrap180HalfOpen(solar.lsDeg - ls0);
      if (moved / dt > 0) rate = moved / dt;
    }
    return Math.abs(wrap180HalfOpen(lsDeg - solar.lsDeg)) <= LS_SCRUB_TOL_DEG;
  }

  /**
   * The sun's latitude over the node off the orrery's pole and place; a moon's
   * row has no season block, which would hold its sun over the equator.
   */
  function subSolarLatDeg(node: OrreryBody): number {
    deps.bodyScene.state.orrery?.propagate();
    _sunScene.set(-node.world.x, -node.world.y, -node.world.z);
    if (_sunScene.lengthSq() === 0) return 0;
    _northScene.copy(YAXIS).applyQuaternion(node.quaternion);
    return THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(_sunScene.normalize().dot(_northScene), -1, 1)));
  }

  /**
   * The plot's, sky's and inspector's declination: the orrery node's, so every row
   * is lit as its tiles are (`softClimate` gives a row with no season block an
   * obliquity of 0). The season model answers where there is no node, no known
   * rotation, no anchor, or a dojo flag asks for it. Readers of another Ls than
   * now (surveys, sweeps) stay on `solarDeclinationDeg`.
   */
  function sunDeclinationDeg(climate: PlotClimate): number {
    const node = deps.bodyScene.state.bodyNode;
    const { legacySun, seasonSun } = deps.dojoPaints.skyDebug;
    if (!node || !node.rotationKnown || legacySun || seasonSun || !solarLatLon()) {
      return solarDeclinationDeg(solar.lsDeg, climate);
    }
    return subSolarLatDeg(node);
  }

  /**
   * Move the clock to the nearest noon within 1 deg of the year's best at this
   * latitude (one year either side); false, clock unmoved, if none clears `minAltDeg`.
   */
  function scrubToSeasonFor(node: OrreryBody, latDeg: number, minAltDeg: number): boolean {
    const t0 = deps.clock.t;
    const step = bodyYearS() / SEASON_SCAN_STEPS;
    const noonAt = (t: number): number => {
      deps.clock.setInstant(t);
      return 90 - Math.abs(latDeg - subSolarLatDeg(node));
    };
    const half = SEASON_SCAN_STEPS / 2;
    const noons: number[] = [];
    for (let i = 0; i <= SEASON_SCAN_STEPS; i++) noons.push(noonAt(t0 + (i - half) * step));
    const best = Math.max(...noons);
    let pick = -1;
    if (best >= minAltDeg) {
      const near = best - Math.min(1, (best - minAltDeg) / 2);
      noons.forEach((q, i) => {
        if (q >= near && (pick < 0 || Math.abs(i - half) < Math.abs(pick - half))) pick = i;
      });
    }
    if (pick < 0) {
      deps.clock.setInstant(t0);
      syncSolar();
      return false;
    }
    // Golden section to the noon's own peak within a sample either side, which
    // is the solstice or the crossing of the latitude the samples straddle.
    let a = t0 + (pick - half - 1) * step;
    let b = t0 + (pick - half + 1) * step;
    const g = (Math.sqrt(5) - 1) / 2;
    for (let i = 0; i < 32; i++) {
      const c = b - g * (b - a);
      const d = a + g * (b - a);
      if (noonAt(c) >= noonAt(d)) b = d; else a = c;
    }
    const peak = noonAt((a + b) / 2);
    if (peak < minAltDeg) {
      deps.clock.setInstant(t0);
      syncSolar();
      return false;
    }
    syncSolar();
    return true;
  }

  /**
   * Scrub the one clock to sunlight over a body point: the nearest instant at
   * which the sun stands at least `minAltDeg` over (`latDeg`, `lonDeg`).
   *
   * Solved rather than swept. The hour angle that puts the sun at a given
   * altitude follows from the sun's latitude over the body at this instant,
   * and `scrubToSolPhase` takes the shortest way to a phase, so the instant
   * this lands on is the nearest one that answers. Every check that needed daylight
   * used to scan for it - the sky arm's own sweep, the ascent check's
   * brightest-of-24 pre-scan, `--hex --claims`'s 24-phase sweep - and a scan
   * that reads the EYE's sun finds no daylight at ORBIT at any phase.
   *
   * The target sits a little inside the daylight window rather than on its
   * edge, where the altitude IS the ask and a readback can land either side of
   * it. Where the latitude is in polar night for this season no hour angle
   * answers and the season itself moves first. An error leaves the clock where
   * it found it.
   */
  function scrubToSunlight(
    latDeg: number, lonDeg: number, minAltDeg: number,
  ): { err: string } | {
    t: number; altitudeDeg: number; requestedDeg: number; solPhase: number; lsDeg: number;
    declinationDeg: number; seasonMoved: boolean; passes: number;
  } {
    const node = deps.bodyScene.state.bodyNode;
    if (!node) return { err: 'no focused body to read the sun off' };
    if (!node.rotationKnown) return { err: `${node.row.id} carries no rotation to turn` };
    syncSolar();
    const a = solarLatLon();
    if (!a) return { err: 'the body carries no reference point the sol phase is counted at' };
    const min = THREE.MathUtils.clamp(minAltDeg, -89, 89);
    const latR = (latDeg * Math.PI) / 180;
    const spin = (node.row.rotation.W_rate_deg_day ?? 0) < 0 ? -1 : 1;
    // The app counts one sol phase, at `a.lon`; this point's own runs ahead of
    // it by its longitude, the other way round on a retrograde rotator.
    const offset = (spin * (lonDeg - a.lon)) / 360;
    const t0 = deps.clock.t;
    const giveUp = (err: string): { err: string } => {
      deps.clock.setInstant(t0);
      syncSolar();
      return { err };
    };
    let seasonMoved = false;
    let pass = 0;
    for (; pass < 6; pass++) {
      const decDeg = subSolarLatDeg(node);
      const decR = (decDeg * Math.PI) / 180;
      const cosH0 = (Math.sin((min * Math.PI) / 180) - Math.sin(latR) * Math.sin(decR))
        / Math.max(1e-9, Math.cos(latR) * Math.cos(decR));
      if (cosH0 > 1) {
        if (seasonMoved || !scrubToSeasonFor(node, latDeg, min)) {
          return giveUp(`the sun never reaches ${min.toFixed(1)} deg over ${latDeg.toFixed(2)}, `
            + `${lonDeg.toFixed(2)} on ${node.row.id} in any season`);
        }
        seasonMoved = true;
        continue;
      }
      const here = wrap01(solar.solPhase + offset);
      if (cosH0 >= -1) {
        // Half the daylight window, as a fraction of the sol; 0.98 of it keeps
        // the target off the edge, where the altitude is the ask exactly.
        const half = (Math.acos(cosH0) / (2 * Math.PI)) * 0.98;
        if (Math.abs(wrap180HalfOpen((here - 0.5) * 360)) > half * 360) {
          const rise = wrap01(0.5 - half);
          const set = wrap01(0.5 + half);
          const dRise = wrap180HalfOpen((rise - here) * 360);
          const dSet = wrap180HalfOpen((set - here) * 360);
          const want = Math.abs(dRise) <= Math.abs(dSet) ? rise : set;
          scrubToSolPhase(wrap01(want - offset));
        }
      }
      const alt = sunAltitudeOverDeg(latDeg, lonDeg);
      if (alt !== null && alt >= min - 1e-3) {
        return {
          t: deps.clock.t, altitudeDeg: alt, requestedDeg: min, solPhase: solar.solPhase,
          lsDeg: solar.lsDeg, declinationDeg: decDeg, seasonMoved, passes: pass + 1,
        };
      }
    }
    const alt = sunAltitudeOverDeg(latDeg, lonDeg);
    return giveUp(`the scrub settled at ${alt === null ? 'no' : `${alt.toFixed(2)} deg of`} sun `
      + `over ${latDeg.toFixed(2)}, ${lonDeg.toFixed(2)} against the ${min.toFixed(1)} deg asked`);
  }

  return {
    plotCtlDebug,
    _litSun,
    _litUp,
    solar,
    solarLatLon,
    syncSolar,
    solsSinceAnchor,
    bodySolarDayS,
    bodyYearS,
    seasonMoves,
    solClockLabel,
    scrubToSolPhase,
    scrubToLs,
    sunDeclinationDeg,
    scrubToSunlight,
    probe: {
      get solDayS(): number { return solDaySeconds(); },
      sunAltitudeOver(latDeg: number, lonDeg: number): number | null {
        return sunAltitudeOverDeg(latDeg, lonDeg);
      },
    },
  };
}
